import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, updateDoc, where } from 'firebase/firestore';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily (last 24 hours)' },
  { value: 'weekly', label: 'Weekly (last 7 days)' },
  { value: 'monthly', label: 'Monthly (last 30 days)' }
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const FALLBACK_TIMEZONES = [
  'UTC',
  'Africa/Nairobi',
  'Africa/Lagos',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney'
];

const DEFAULT_ALERT_SETTINGS = {
  enabled: false,
  frequency: 'daily',
  weeklyDay: 1,
  monthlyDay: 1,
  timezone: 'UTC',
  sendHour: 8,
  speedThreshold: 60,
  ccManagerIds: [],
  lastSentAt: null,
  lastSentDate: null,
  lastAnomalyCount: null
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, idx) => {
  const label = idx === 0 ? '12:00 AM (midnight)'
    : idx < 12 ? `${idx}:00 AM`
    : idx === 12 ? '12:00 PM (noon)'
    : `${idx - 12}:00 PM`;
  return { value: idx, label };
});

const getDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_err) {
    return 'UTC';
  }
};

const getTimezoneOptions = () => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const list = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch (_err) {
    // ignore
  }
  return FALLBACK_TIMEZONES;
};

const formatLastSentAt = (value) => {
  if (!value) return 'Never';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userDocId, setUserDocId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [twitterUsername, setTwitterUsername] = useState('');
  const [alertSettings, setAlertSettings] = useState({ ...DEFAULT_ALERT_SETTINGS, timezone: getDefaultTimezone() });
  const [managerOptions, setManagerOptions] = useState([]);

  const isStaff = userRole === 'manager' || userRole === 'administrator';
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  useEffect(() => {
    const loadSettings = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        let resolvedDocId = user.uid;

        if (!userDoc.exists()) {
          const usersRef = collection(db, 'users');
          const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
          const querySnapshot = await getDocs(uidQuery);
          if (!querySnapshot.empty) {
            userDoc = querySnapshot.docs[0];
            resolvedDocId = querySnapshot.docs[0].id;
          }
        }

        if (!userDoc.exists()) {
          setError('Unable to load user settings.');
          return;
        }

        setUserDocId(resolvedDocId);
        const userData = userDoc.data();
        setUserRole(userData.role || '');
        setTwitterUsername(userData.preferredTwitterUsername || '');

        const persisted = userData.alertSettings || {};
        setAlertSettings({
          ...DEFAULT_ALERT_SETTINGS,
          timezone: persisted.timezone || getDefaultTimezone(),
          ...persisted,
          ccManagerIds: Array.isArray(persisted.ccManagerIds) ? persisted.ccManagerIds : []
        });
      } catch (err) {
        console.error('Error loading settings:', err);
        setError('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [navigate]);

  useEffect(() => {
    const loadManagers = async () => {
      if (!isStaff) return;
      try {
        const usersRef = collection(db, 'users');
        let snapshot;
        try {
          snapshot = await getDocs(query(usersRef, where('role', '==', 'manager'), orderBy('firstName', 'asc')));
        } catch (_err) {
          snapshot = await getDocs(query(usersRef, where('role', '==', 'manager')));
        }
        const list = snapshot.docs
          .map((managerDoc) => {
            const data = managerDoc.data();
            const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
            return {
              id: managerDoc.id,
              label: fullName || data.email || 'Unnamed Manager',
              email: data.email || ''
            };
          })
          .filter((manager) => manager.id !== userDocId)
          .sort((a, b) => a.label.localeCompare(b.label));
        setManagerOptions(list);
      } catch (err) {
        console.error('Error loading managers for CC list:', err);
      }
    };

    if (userDocId) loadManagers();
  }, [isStaff, userDocId]);

  const updateAlertField = (field, value) => {
    setAlertSettings((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCcManager = (managerId) => {
    setAlertSettings((prev) => {
      const current = Array.isArray(prev.ccManagerIds) ? prev.ccManagerIds : [];
      const next = current.includes(managerId)
        ? current.filter((id) => id !== managerId)
        : [...current, managerId];
      return { ...prev, ccManagerIds: next };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!userDocId) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const sanitizedTwitter = twitterUsername.replace(/^@/, '').trim();
      const payload = {
        preferredTwitterUsername: sanitizedTwitter,
        updatedAt: new Date().toISOString()
      };

      if (isStaff) {
        const speedThresholdNumber = Number(alertSettings.speedThreshold);
        const sendHourNumber = Number(alertSettings.sendHour);
        const sanitizedAlertSettings = {
          enabled: Boolean(alertSettings.enabled),
          frequency: ['daily', 'weekly', 'monthly'].includes(alertSettings.frequency)
            ? alertSettings.frequency
            : 'daily',
          weeklyDay: Number.isInteger(Number(alertSettings.weeklyDay))
            ? Math.min(6, Math.max(0, Number(alertSettings.weeklyDay)))
            : 1,
          monthlyDay: Number.isInteger(Number(alertSettings.monthlyDay))
            ? Math.min(31, Math.max(1, Number(alertSettings.monthlyDay)))
            : 1,
          timezone: alertSettings.timezone || getDefaultTimezone(),
          sendHour: Number.isFinite(sendHourNumber)
            ? Math.min(23, Math.max(0, Math.floor(sendHourNumber)))
            : 8,
          speedThreshold: Number.isFinite(speedThresholdNumber) && speedThresholdNumber > 0
            ? speedThresholdNumber
            : 60,
          ccManagerIds: Array.isArray(alertSettings.ccManagerIds) ? alertSettings.ccManagerIds : [],
          lastSentAt: alertSettings.lastSentAt || null,
          lastSentDate: alertSettings.lastSentDate || null,
          lastAnomalyCount: typeof alertSettings.lastAnomalyCount === 'number'
            ? alertSettings.lastAnomalyCount
            : null
        };
        payload.alertSettings = sanitizedAlertSettings;
      }

      await updateDoc(doc(db, 'users', userDocId), payload);
      setTwitterUsername(sanitizedTwitter);
      setSuccess('Settings saved.');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-[#1a1f2e] mb-2">Settings</h1>
      <p className="text-gray-600 mb-6">Manage your personal dashboard preferences.</p>

      <form onSubmit={handleSave} className="space-y-6">
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[#1a1f2e]">Dashboard News Source</h2>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Preferred Twitter/X Username for News
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">@</span>
              <input
                type="text"
                value={twitterUsername}
                onChange={(e) => setTwitterUsername(e.target.value)}
                placeholder="Ma3Route"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Example: <span className="font-medium">Ma3Route</span>. Leave blank to use default account.
            </p>
          </div>
        </section>

        {isStaff && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-[#1a1f2e]">Driver Anomaly Email Digest</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Get a compiled report of speeding and harsh-driving anomalies for the drivers under your supervision.
                  Sent by email and to your in-app Messages inbox.
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={Boolean(alertSettings.enabled)}
                  onChange={(e) => updateAlertField('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <span className="relative w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#00B8B8] transition-colors">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${alertSettings.enabled ? 'translate-x-5' : ''}`}></span>
                </span>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {alertSettings.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Frequency</label>
                <select
                  value={alertSettings.frequency}
                  onChange={(e) => updateAlertField('frequency', e.target.value)}
                  disabled={!alertSettings.enabled}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {FREQUENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                <select
                  value={alertSettings.timezone}
                  onChange={(e) => updateAlertField('timezone', e.target.value)}
                  disabled={!alertSettings.enabled}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Used to determine when weekly/monthly digests are due.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Earliest send time (local)</label>
                <select
                  value={alertSettings.sendHour}
                  onChange={(e) => updateAlertField('sendHour', Number(e.target.value))}
                  disabled={!alertSettings.enabled}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {HOUR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Digest is sent on the first cron run at or after this hour in your timezone.
                  The cron currently runs daily at 08:30 EAT (05:30 UTC).
                </p>
              </div>

              {alertSettings.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Day of week</label>
                  <select
                    value={alertSettings.weeklyDay}
                    onChange={(e) => updateAlertField('weeklyDay', Number(e.target.value))}
                    disabled={!alertSettings.enabled}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {WEEKDAY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {alertSettings.frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Day of month</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={alertSettings.monthlyDay}
                    onChange={(e) => updateAlertField('monthlyDay', Number(e.target.value))}
                    disabled={!alertSettings.enabled}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Days 29-31 fall back to the last day of the month.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Speed threshold (km/h)</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={alertSettings.speedThreshold}
                  onChange={(e) => updateAlertField('speedThreshold', e.target.value)}
                  disabled={!alertSettings.enabled}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Any vehicle speed above this value is flagged in addition to other anomalies.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">CC managers</label>
              {managerOptions.length === 0 ? (
                <p className="text-sm text-gray-500">No other managers available to copy.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {managerOptions.map((manager) => (
                    <label
                      key={manager.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={Array.isArray(alertSettings.ccManagerIds) && alertSettings.ccManagerIds.includes(manager.id)}
                        onChange={() => toggleCcManager(manager.id)}
                        disabled={!alertSettings.enabled}
                        className="w-4 h-4 text-[#00B8B8] rounded border-gray-300 focus:ring-[#00E0E0]"
                      />
                      <span className="flex-1 truncate">
                        <span className="text-gray-800">{manager.label}</span>
                        {manager.email && (
                          <span className="text-xs text-gray-500 ml-1">({manager.email})</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Selected managers will receive a copy of the email and gain access to the digest in their Messages inbox.
              </p>
            </div>

            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
              <div>
                <span className="font-semibold">Last sent:</span> {formatLastSentAt(alertSettings.lastSentAt)}
                {typeof alertSettings.lastAnomalyCount === 'number' && (
                  <span className="ml-2 text-blue-700">({alertSettings.lastAnomalyCount} anomalies)</span>
                )}
              </div>
              <p className="text-xs text-blue-800 mt-2">
                The cron runs once a day at 08:30 EAT (05:30 UTC). Your digest is sent on that run if the local hour in your timezone is at or after the earliest send time you selected, and only on days that match your chosen frequency.
              </p>
            </div>
          </section>
        )}

        {error && <div className="text-sm text-red-600">{error}</div>}
        {success && <div className="text-sm text-green-600">{success}</div>}

        <div>
          <button
            type="submit"
            disabled={saving}
            className={`px-5 py-2 rounded-lg text-white font-semibold transition-colors ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#00B8B8] hover:bg-[#00A3A3]'
            }`}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
